import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

// Dot colors for the multi-colored Big 6 domino
const dotColors = ['#38bdf8', '#f472b6', '#a78bfa', '#34d399', '#fbbf24', '#fb923c'];

function DominoIcon6() {
  // 6-6 domino with multi-colored dots
  const dotPositions = [
    // Standard 6-dot layout: 3 rows x 2 columns
    { x: 6, y: 4 }, { x: 18, y: 4 },
    { x: 6, y: 12 }, { x: 18, y: 12 },
    { x: 6, y: 20 }, { x: 18, y: 20 },
  ];
  return (
    <svg viewBox="0 0 52 24" className="w-28 h-14 drop-shadow-lg">
      {/* Left half */}
      <rect x="0" y="0" width="24" height="24" rx="3" fill="white" />
      {dotPositions.map((p, i) => (
        <circle key={`l${i}`} cx={p.x} cy={p.y} r="2.5" fill={dotColors[i]} />
      ))}
      {/* Divider */}
      <line x1="26" y1="2" x2="26" y2="22" stroke="#cbd5e1" strokeWidth="0.5" />
      {/* Right half */}
      <rect x="28" y="0" width="24" height="24" rx="3" fill="white" />
      {dotPositions.map((p, i) => (
        <circle key={`r${i}`} cx={p.x + 28} cy={p.y} r="2.5" fill={dotColors[(i + 3) % 6]} />
      ))}
    </svg>
  );
}

function DominoIcon12() {
  // 12-12 domino with dots in a 3x4 grid
  const dotPositions: { x: number; y: number }[] = [];
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 3; col++) {
      dotPositions.push({ x: 5 + col * 7, y: 4 + row * 6 });
    }
  }
  return (
    <svg viewBox="0 0 56 28" className="w-28 h-14 drop-shadow-lg">
      {/* Left half */}
      <rect x="0" y="0" width="26" height="28" rx="3" fill="white" />
      {dotPositions.map((p, i) => (
        <circle key={`l${i}`} cx={p.x} cy={p.y} r="2" fill="#38bdf8" />
      ))}
      {/* Divider */}
      <line x1="28" y1="2" x2="28" y2="26" stroke="#cbd5e1" strokeWidth="0.5" />
      {/* Right half */}
      <rect x="30" y="0" width="26" height="28" rx="3" fill="white" />
      {dotPositions.map((p, i) => (
        <circle key={`r${i}`} cx={p.x + 30} cy={p.y} r="2" fill="#38bdf8" />
      ))}
    </svg>
  );
}

type GameEntry = {
  id: string;
  name: string;
  description: string;
  players: string;
  icon?: string;
  customIcon?: React.ReactNode;
  gradient: string;
};

const games: GameEntry[] = [
  {
    id: 'spades',
    name: 'Spades',
    description: '4-player trick-taking game with bidding',
    players: '4 Players',
    icon: '\u2660',
    gradient: 'from-shark-700 to-shark-900',
  },
  {
    id: 'bid_whist',
    name: 'Bid Whist',
    description: 'Classic partnership card game',
    players: '4 Players',
    icon: '\u2663',
    gradient: 'from-ocean-800 to-ocean-950',
  },
  {
    id: 'tonk',
    name: 'Tonk',
    description: 'Fast-paced matching card game',
    players: '2-4 Players',
    icon: '\u2665',
    gradient: 'from-shark-800 to-abyss-850',
  },
  {
    id: 'dominoes_big6',
    name: 'Dominoes (Big 6)',
    description: 'Classic domino game with double-6 set',
    players: '2-4 Players',
    customIcon: <DominoIcon6 />,
    gradient: 'from-ocean-700 to-shark-800',
  },
  {
    id: 'dominoes_big12',
    name: 'Dominoes (Big 12)',
    description: 'Extended domino game with double-12 set',
    players: '2-4 Players',
    customIcon: <DominoIcon12 />,
    gradient: 'from-shark-700 to-ocean-900',
  },
];

export default function HomePage() {
  const { isAuthenticated, loginAsGuest } = useAuthStore();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 px-4 ocean-bubbles relative overflow-hidden">
        {/* Bull shark background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src="/cardsharkie-logo.png"
            alt=""
            className="w-[500px] h-[500px] opacity-[0.07] blur-[1px]"
          />
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <img
            src="/cardsharkie-logo.png"
            alt="CardSharkie"
            className="w-32 h-32 mx-auto mb-6 rounded-full ring-4 ring-shark-400/30 shadow-[0_0_60px_rgba(14,165,233,0.3)]"
          />
          <h1 className="text-5xl font-bold mb-6">
            Play Classic Card Games <span className="brand-text">Online</span>
          </h1>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Join thousands of players in Spades, Bid Whist, Tonk, and Dominoes.
            Play with friends or match with players worldwide.
          </p>
          <div className="flex justify-center space-x-4">
            {isAuthenticated ? (
              <Link
                to="/lobby"
                className="bg-shark-500 hover:bg-shark-600 text-white text-lg px-8 py-4 rounded-lg font-semibold"
              >
                Play Now
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  className="bg-shark-500 hover:bg-shark-600 text-white text-lg px-8 py-4 rounded-lg font-semibold"
                >
                  Create Account
                </Link>
                <button
                  onClick={loginAsGuest}
                  className="bg-gray-600 hover:bg-gray-700 text-white text-lg px-8 py-4 rounded-lg font-semibold"
                >
                  Play as Guest
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Wave divider */}
      <div className="wave-divider"></div>

      {/* Games Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Our Games</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <div
                key={game.id}
                className="glass-card-dark overflow-hidden hover:scale-105 transition-transform"
              >
                <div className={`h-36 bg-gradient-to-br ${game.gradient} flex items-center justify-center`}>
                  {game.customIcon ? game.customIcon : <span className="text-5xl">{game.icon}</span>}
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-semibold mb-2">{game.name}</h3>
                  <p className="text-gray-400 text-sm mb-3">{game.description}</p>
                  <p className="text-shark-400 text-sm">{game.players}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave divider */}
      <div className="wave-divider"></div>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Play With Us?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 bg-shark-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎮</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Quick Matchmaking</h3>
              <p className="text-gray-400">Find games instantly with skill-based matchmaking</p>
            </div>
            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 bg-shark-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🏆</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Competitive Rankings</h3>
              <p className="text-gray-400">Climb the leaderboards and earn achievements</p>
            </div>
            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 bg-shark-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">👥</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Play with Friends</h3>
              <p className="text-gray-400">Create private rooms and invite your friends</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
